import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { 
  Users, 
  FileText, 
  CalendarDays,
  LogOut, 
  Menu,
  X,
  Shield,
  Settings,
  User,
  ChevronDown
} from 'lucide-react';
import { useState, useRef, useEffect } from 'react';
import { BUILD_VERSION } from '@/version';
import gpsLogo from '@/GPS-logo-2022.svg';

export function Layout() {
  const { user, logout, isAdmin, isApprover } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);

  const navigation = [
    { name: 'Kontakte', href: '/', icon: Users },
    { name: 'Kalender', href: '/calendar', icon: CalendarDays },
    { name: 'Meine Anträge', href: '/proposals', icon: FileText },
  ];

  // Close user menu when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setIsUserMenuOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const isActive = (href: string) => {
    if (href === '/') {
      return location.pathname === '/' || location.pathname.startsWith('/contacts/');
    }
    return location.pathname.startsWith(href);
  };

  const handleLogout = () => {
    setIsUserMenuOpen(false);
    logout();
  };

  const handleNavigate = (path: string) => {
    setIsUserMenuOpen(false);
    navigate(path);
  };

  return (
    <div className="min-h-screen bg-gray-50 pb-[env(safe-area-inset-bottom)]">
      {/* Navigation */}
      <nav className="bg-white shadow-sm border-b border-gray-200 pt-[env(safe-area-inset-top)]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-20">
            <div className="flex">
              <div className="flex-shrink-0 flex items-center">
                <Link to="/" className="flex items-center focus:outline-none">
                  <img
                    src={gpsLogo}
                    alt="GPS"
                    className="h-12 w-auto"
                  />
                </Link>
              </div>
              
              <div className="hidden sm:ml-6 sm:flex sm:space-x-8">
                {navigation.map((item) => (
                  <Link
                    key={item.name}
                    to={item.href}
                    className={`inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium ${
                      isActive(item.href)
                        ? 'border-primary-600 text-gray-900'
                        : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
                    }`}
                  >
                    <item.icon className="h-4 w-4 mr-2" />
                    {item.name}
                  </Link>
                ))}
              </div>
            </div>
            
            <div className="flex items-center gap-3">
              {/* User Menu Dropdown */}
              <div className="relative" ref={userMenuRef}>
                <button
                  onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                  className="flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-md transition-colors"
                >
                  <div className="text-right hidden sm:block">
                    <p className="font-medium">{user?.name}</p>
                    <p className="text-xs text-gray-500">
                      {user?.role === 'admin' ? 'Administrator' :
                       user?.role === 'approver' ? 'Genehmiger' : 'Benutzer'}
                    </p>
                  </div>
                  <div className="h-8 w-8 rounded-full bg-primary-100 flex items-center justify-center">
                    <span className="text-primary-600 font-medium text-sm">
                      {user?.name?.charAt(0).toUpperCase()}
                    </span>
                  </div>
                  <ChevronDown className={`h-4 w-4 text-gray-400 transition-transform ${isUserMenuOpen ? 'rotate-180' : ''}`} />
                </button>
                
                {/* Dropdown Menu */}
                {isUserMenuOpen && (
                  <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50">
                    {/* User Info Header */}
                    <div className="px-4 py-3 border-b border-gray-100">
                      <p className="text-sm font-medium text-gray-900">{user?.name}</p>
                      <p className="text-sm text-gray-500">{user?.email}</p>
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium mt-2 ${
                        user?.role === 'admin' ? 'bg-purple-100 text-purple-800' :
                        user?.role === 'approver' ? 'bg-blue-100 text-blue-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {user?.role === 'admin' ? 'Administrator' :
                         user?.role === 'approver' ? 'Genehmiger' : 'Benutzer'}
                      </span>
                    </div>
                    
                    {/* Menu Items */}
                    <div className="py-1">
                      {/* Admin Section for Approvers+ */}
                      {isApprover && (
                        <>
                          <div className="px-4 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                            Administration
                          </div>
                          <button
                            onClick={() => handleNavigate('/admin')}
                            className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center"
                          >
                            <Shield className="h-4 w-4 mr-3 text-gray-400" />
                            Benutzerverwaltung
                          </button>
                        </>
                      )}
                      
                      {/* Settings for Admins only */}
                      {isAdmin && (
                        <button
                          onClick={() => handleNavigate('/admin?tab=settings')}
                          className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center"
                        >
                          <Settings className="h-4 w-4 mr-3 text-gray-400" />
                          Systemeinstellungen
                        </button>
                      )}
                      
                      {(isApprover || isAdmin) && <div className="border-t border-gray-100 my-1" />}
                      
                      {/* Profile */}
                      <button
                        onClick={() => handleNavigate('/profile')}
                        className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center"
                      >
                        <User className="h-4 w-4 mr-3 text-gray-400" />
                        Profil
                      </button>
                      
                      <div className="border-t border-gray-100 my-1" />
                      
                      {/* Logout */}
                      <button
                        onClick={handleLogout}
                        className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center"
                      >
                        <LogOut className="h-4 w-4 mr-3" />
                        Abmelden
                      </button>
                    </div>
                  </div>
                )}
              </div>
              
              {/* Mobile menu button */}
              <button
                className="sm:hidden ml-2 p-2 text-gray-400 hover:text-gray-500"
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              >
                {isMobileMenuOpen ? (
                  <X className="h-6 w-6" />
                ) : (
                  <Menu className="h-6 w-6" />
                )}
              </button>
            </div>
          </div>
        </div>
        
        {/* Mobile menu */}
        {isMobileMenuOpen && (
          <div className="sm:hidden">
            <div className="pt-2 pb-3 space-y-1">
              {navigation.map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`block pl-3 pr-4 py-2 border-l-4 text-base font-medium ${
                    isActive(item.href)
                      ? 'bg-primary-50 border-primary-600 text-primary-700'
                      : 'border-transparent text-gray-500 hover:bg-gray-50 hover:border-gray-300 hover:text-gray-700'
                  }`}
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  <div className="flex items-center">
                    <item.icon className="h-4 w-4 mr-2" />
                    {item.name}
                  </div>
                </Link>
              ))}
              
              {/* Mobile Admin Links */}
              {isApprover && (
                <>
                  <div className="border-t border-gray-200 my-2" />
                  <Link
                    to="/admin"
                    className={`block pl-3 pr-4 py-2 border-l-4 text-base font-medium ${
                      location.pathname.startsWith('/admin')
                        ? 'bg-primary-50 border-primary-600 text-primary-700'
                        : 'border-transparent text-gray-500 hover:bg-gray-50 hover:border-gray-300 hover:text-gray-700'
                    }`}
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    <div className="flex items-center">
                      <Shield className="h-4 w-4 mr-2" />
                      Verwaltung
                    </div>
                  </Link>
                </>
              )}
            </div>
          </div>
        )}
      </nav>

      {/* Main content */}
      <main className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        <div className="px-4 sm:px-0">
          <Outlet />
        </div>
      </main>
      
      {/* Build version */}
      <footer className="fixed bottom-0 left-0 right-0 py-1 text-center">
        <span className="text-[10px] text-gray-400 select-none">
          v{BUILD_VERSION}
        </span>
      </footer>
    </div>
  );
}
